<template>
  <div>
    <div class="warranty-card">
      <div class="d-flex">
        <text-field
          v-model="formData.name"
          name="name"
          label="*Program Name"
          placeholder="Enter Program Name"
          :validation-errors="v$.$errors"
          label-class="flex-2"
          class="mr-3 full-width"
        />
      </div>
      <div
        class="warranty-additional-info relative z5 flex flex-col md:flex-row gap-3 w-full *:flex-1"
      >
        <choice-box
          v-model="provider_name"
          v-validate="'required'"
          name="provider_name"
          label="*Provider"
          type="warranty_providers"
          placeholder="Select Provider"
          tooltip-text="If no Display Name is provided, this name will be shown to customers. This must remain the true warranty provider and is required for reporting and warranty sales transmission."
          :validation-errors="v$.$errors"
        />
        <text-field
          v-model="formData.display_name"
          name="display_name"
          label="Provider Display Name"
          placeholder="Custom name to display instead of provider name"
          tooltip-text="If entered, this name will be shown to customers for warranty branding."
          :validation-errors="v$.$errors"
        />
      </div>
      <div class="mb-3">
        <text-field
          v-model="formData.sku_number"
          v-validate="'required'"
          name="sku_number"
          label="*SKU Number (Product ID)"
          placeholder="Enter SKU Number"
          :validation-errors="v$.$errors"
          class="full-width"
        />
      </div>
      <text-area
        v-model="formData.description"
        class="mb-3"
        name="description"
        :rows="5"
        placeholder="Description"
        :validation-errors="v$.$errors"
        label="*Coverage Description"
      />
      <div class="relative z3 mb-3">
        <div class="flex flex-col md:flex-row gap-3">
          <text-field
            v-model="labor_rate"
            name="labor_rate"
            label="Labor Rate"
            placeholder="Enter Labor Rate"
            :validation-errors="v$.$errors"
            label-class="flex-1"
            class="mr-3"
            currency
          />
          <text-field
            v-model="cost"
            name="cost"
            label="*Cost"
            placeholder="Enter Cost"
            tooltip-text="The price to purchase the warranty program from the warranty provider"
            :validation-errors="v$.$errors"
            label-class="flex-1"
            class="mr-3"
            currency
          />
          <text-field
            v-model="price"
            name="price"
            label="*Purchase Price"
            placeholder="Enter Purchase Price"
            tooltip-text="The price the customer pays to purchase the warranty"
            :validation-errors="v$.$errors"
            label-class="flex-1"
            class="mr-3"
            currency
          />
        </div>
        <div class="mt-1">
          <sera-checkbox
            :checked="formData.taxable"
            @change="({ detail }) => (formData.taxable = detail)"
          >
            <strong>Taxable</strong>
          </sera-checkbox>
        </div>
        <div class="warranty-additional-info relative z3 w-50">
          <Select
            v-model="formData.term_years"
            name="Term (Years)"
            label="*Term (Years)"
            :options="termYears"
            placeholder="Select Term"
            :field-error="firstErrorMessage(v$.formData?.term_years)"
            class="mb-2 mr-3 flex-1"
          />
          <text-field
            v-model.number="formData.effective_after_days"
            name="effective_after_days"
            label="*Effective After (Days)"
            placeholder="Enter Effective After"
            tooltip-text="How many days until the warranty program is effective after the purchase date"
            labelClass="flex-1"
            type="number"
            :validation-errors="v$.$errors"
          />
        </div>
      </div>
      <div class="d-flex mb-4 relative">
        <Select
          v-if="showAccountingCategories"
          v-model="formData.accounting_category_id"
          name="Default Accounting Category"
          :options="accountingCategoriesOptions"
          placeholder="Select Default Accounting Category"
          :field-error="firstErrorMessage(v$.formData?.accounting_category_id)"
          class="flex-1 mr-3"
        />
        <Select
          v-if="showAccountingClasses"
          v-model="formData.accounting_class_id"
          name="Default Accounting Class"
          :options="accountingClassOptions"
          placeholder="Select Default Accounting Class"
          :field-error="firstErrorMessage(v$.formData?.accounting_class_id)"
          class="flex-1"
        >
          <template #item-row="{ option }">
            <div class="option-row">
              <span :class="{ 'strike-through': option.archived }">
                {{ option.label }}
              </span>
            </div>
          </template>
        </Select>
      </div>
      <div>
        <Select
          v-model="formData.equipment_type_ids"
          data-cy="add-equipment-types"
          :options="equipmentTypeOptions"
          multiple
          search
          full-width
          label="Add Equipment Types"
          class="mb-4"
        />
      </div>
      <div class="button-container mt-3">
        <Button type="secondary" @click="returnToWarrantiesPage()">
          <span>Cancel</span>
        </Button>
        <Button :disabled="saving" @click="handleSave()">
          <span v-if="!saving">Save Warranty</span>
          <Spinner v-else message="Saving..." />
        </Button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { getProperty } from "@/lib/helpers.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Button from "@/components/Button.vue";
import ChoiceBox from "@/components/ChoiceBox.vue";
import Spinner from "@/components/Spinner.vue";
import TextArea from "@/components/TextArea.vue";
import TextField from "@/components/TextField.vue";
import CONSTANTS from "@/Constants.js";
import { sortEquipmentTypes } from "@/vuex/modules/equipmentTypes";
import useVuelidate from "@vuelidate/core";
import { helpers, maxLength, minValue, required } from "@vuelidate/validators";

export default {
  components: {
    ChoiceBox,
    Spinner,
    Button,
    TextField,
    TextArea
  },
  setup() {
    return {
      v$: useVuelidate(),
      firstErrorMessage
    };
  },
  validations() {
    return {
      formData: {
        name: { required, $autoDirty: true },
        sku_number: { required, $autoDirty: true },
        description: { required, $autoDirty: true },
        term_years: { required, $autoDirty: true },
        effective_after_days: {
          required,
          $autoDirty: true,
          isNumber: helpers.regex(/^\d*$/),
          maxLength: maxLength(3),
          minValue: minValue(0)
        }
      },
      provider_name: { required, $autoDirty: true },
      labor_rate: { required, $autoDirty: true },
      cost: { required, $autoDirty: true },
      price: { required, $autoDirty: true }
    };
  },
  data() {
    return {
      saving: false,
      formData: {
        taxable: true
      },
      provider_name: null,
      labor_rate: 0,
      cost: 0,
      price: 0,
      requestTypeOptions: [
        {
          value: "any",
          label: "Any"
        },
        {
          value: "all",
          label: "All"
        }
      ]
    };
  },
  computed: {
    ...mapGetters([
      "accountingClassOptions",
      "showAccountingCategories",
      "showAccountingClasses"
    ]),
    ...mapState(["currentWarrantyProgram", "accountingCategories"]),
    ...mapState({
      accountingCategoriesOptions: ({ accountingCategories }) =>
        accountingCategories.map((accountingCategory) => ({
          value: accountingCategory.id,
          label: accountingCategory.name
        })),
      jbwIntegrationEnabled({ tenant }) {
        return getProperty(tenant, "jb_warranties_enabled");
      },
      isEditing(currentWarrantyProgram) {
        return currentWarrantyProgram && currentWarrantyProgram.id;
      },
      equipmentTypeOptions({ equipmentTypes }) {
        return sortEquipmentTypes(equipmentTypes)
          .filter((equipmentType) => !equipmentType.archived)
          .map((equipmentType) => ({
            value: equipmentType.id,
            label: equipmentType.name
          }));
      }
    }),
    termYears() {
      return Array.from({ length: 15 }, (_, i) => ({
        label: i + 1,
        value: i + 1
      }));
    }
  },
  watch: {
    currentWarrantyProgram(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setFormData();
      }
    },
    jbwIntegrationEnabled(newValue) {
      if (newValue && !this.isEditing) {
        // Check for jbw integration
        // if enabled default provider_name on create to JB warranties
        this.provider_name = CONSTANTS.JBW_WARRANTY_PROVIDER_NAME;
      }
    }
  },
  mounted() {
    if (!this.accountingCategories.length) {
      this.fetchAccountingCategories();
    }
    if (!this.accountingClassOptions.length) {
      this.fetchAccountingClasses();
    }
    this.fetchEquipmentTypes();

    if (this.currentWarrantyProgram) {
      this.setFormData();
    }
  },
  methods: {
    ...mapActions([
      "fetchWarrantyProgram",
      "createWarrantyProgram",
      "updateWarrantyProgram",
      "fetchAccountingCategories",
      "fetchAccountingClasses",
      "fetchEquipmentTypes"
    ]),
    setFormData() {
      this.formData = {
        ...this.formData,
        ...this.currentWarrantyProgram,
        equipment_type_ids: this.currentWarrantyProgram.equipment_types.map(
          (et) => et.id
        )
      };
      this.provider_name = this.currentWarrantyProgram.provider_name;
      this.labor_rate = this.currentWarrantyProgram.labor_rate_cents;
      this.cost = this.currentWarrantyProgram.cost_cents;
      this.price = this.currentWarrantyProgram.price_cents;
    },
    returnToWarrantiesPage() {
      window.location.href = this.warrantyProgramIndexPath();
    },
    handleSave() {
      const upsert = this.currentWarrantyProgram
        ? this.updateWarrantyProgram
        : this.createWarrantyProgram;
      const payload = {
        ...this.formData,
        provider_name: this.provider_name,
        labor_rate_cents: this.labor_rate,
        cost_cents: this.cost,
        price_cents: this.price,
        equipment_type_ids: this.formData.equipment_type_ids || [],
        equipment_request_kind: "all"
      };
      if (this.currentWarrantyProgram && this.currentWarrantyProgram.id) {
        payload.id = this.currentWarrantyProgram.id;
      }
      this.v$.$validate().then((valid) => {
        if (valid) {
          this.saving = true;
          upsert(payload)
            .then(() => {
              window.notify.success("Warranty saved!");
              this.saving = false;
              this.returnToWarrantiesPage();
            })
            .catch(() => (this.saving = false));
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.flex-2 {
  flex: 2;
}

.button-container {
  display: flex;
  justify-content: space-between;
}

.warranty-card {
  max-width: 815px;
}

.tooltip-text {
  white-space: normal;
}

.warranty-additional-info {
  display: flex;
  position: relative;
}

.request-radio-group :deep(.radio-group) {
  gap: 15px;
  display: flex;
  font-size: 0.8em;
}
</style>
